import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table, Spin, message, Descriptions, Tag } from 'antd';
import { UserOutlined, MedicineBoxOutlined, TeamOutlined, ShoppingOutlined } from '@ant-design/icons';
import api from '../../services/api';

const DoctorDetails = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        fetchDoctorAnalytics();
    }, [id]);

    const fetchDoctorAnalytics = async () => {
        try {
            const response = await api.get(`/medicalseller/doctor/${id}/analytics`);
            setAnalytics(response.data.data);
        } catch (error) {
            message.error('Failed to load doctor analytics');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    const patientColumns = [
        {
            title: 'Patient Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        }
    ];

    const drugColumns = [
        {
            title: 'Drug Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Times Prescribed',
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count,
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <h1 style={{ fontSize: '28px', marginBottom: '24px' }}>
                Doctor Details & Analytics
            </h1>

            {/* Doctor Information */}
            <Card title="Doctor Information" style={{ marginBottom: '24px' }}>
                <Descriptions bordered>
                    <Descriptions.Item label="Name">{analytics?.doctor.name}</Descriptions.Item>
                    <Descriptions.Item label="Specialization">{analytics?.doctor.specialization}</Descriptions.Item>
                    <Descriptions.Item label="Phone Number">{analytics?.doctor.phoneNumber}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Patients"
                            value={analytics?.analytics.totalPatients}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Visits"
                            value={analytics?.analytics.totalVisits}
                            prefix={<MedicineBoxOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Drugs Prescribed"
                            value={analytics?.analytics.totalDrugsPrescribed}
                            prefix={<ShoppingOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Unique Drugs"
                            value={analytics?.analytics.topPrescribedDrugs?.length || 0}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Top Prescribed Drugs */}
            <Card title="Top Prescribed Drugs" style={{ marginBottom: '24px' }}>
                <Table
                    columns={drugColumns}
                    dataSource={analytics?.analytics.topPrescribedDrugs || []}
                    rowKey="name"
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Patients List */}
            <Card title="Patients">
                <Table
                    columns={patientColumns}
                    dataSource={analytics?.analytics.patients || []}
                    rowKey="_id"
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default DoctorDetails;
